import api from './axios';
import {
  ProfileResponse,
  MembershipStatus,
  RewardPoint,
  ReferralCodeResponse,
  WalletResponse,
} from '../types/profile';

export const getProfile = async (): Promise<ProfileResponse> => {
  const response = await api.get<ProfileResponse>('/api/shop/profile');
  return response.data;
};

export const getMembershipSummary = async (): Promise<MembershipStatus> => {
  const response = await api.get<MembershipStatus>('/api/shop/membership/summary');
  return response.data;
};

export const getRewardPointsSummary = async (): Promise<RewardPoint> => {
  const response = await api.get<RewardPoint>('/api/reward-points/summary');
  return response.data;
};

export interface PointsRedemptionResponse {
  isValid: boolean;
  pointsToRedeem: number;
  discountAmount: number;
}

export interface RewardPointsSettings {
  isActive: boolean;
  earnRateAmount: number;
  earnRatePoints: number;
  redeemRatePoints: number;
  redeemRateAmount: number;
  minimumRedemption: number | null;
  maximumRedemption: number | null;
  expiryType: string;
  pointsExpiryDays: number;
  description: string;
}

export const calculatePointsRedemption = async (pointsToRedeem: number): Promise<PointsRedemptionResponse> => {
  const response = await api.post<PointsRedemptionResponse>('/api/reward-points/calculate-redemption', {
    pointsToRedeem
  });
  return response.data;
};

export const getRewardPointsSettings = async (): Promise<RewardPointsSettings> => {
  const response = await api.get<RewardPointsSettings>('/api/reward-points/settings');
  return response.data;
};

export interface ReferralCode {
  id: number;
  customerId: string;
  code: string;
  isActive: boolean;
  usageCount: number;
  maxUsage: number | null;
  createdAt: string;
  updatedAt: string;
}

export const getReferralCode = async (): Promise<ReferralCodeResponse> => {
  try {
    const response = await api.get('/api/shop/referral-code');
    return response.data;
  } catch (error) {
    console.error('Error fetching referral code:', error);
    throw error;
  }
};

export interface UpdateProfileData {
  firstName: string;
  lastName: string;
  phone: string;
  birthMonth: string;
  birthDay: string;
  birthYear: string;
  gender: string;
}

export const updateProfile = async (data: UpdateProfileData): Promise<ProfileResponse> => {
  try {
    const response = await api.patch<ProfileResponse>('/api/shop/profile', data);
    return response.data;
  } catch (error) {
    console.error('Error updating profile:', error);
    throw error;
  }
};

export const addWalletCard = async (): Promise<WalletResponse> => {
  try {
    const response = await api.post<WalletResponse>('/api/wallet/add-card');
    
    // Validate the response
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create wallet card');
    }
    
    if (!response.data.pass || !response.data.pass.downloadUrl) {
      throw new Error('Invalid wallet pass response');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error adding wallet card:', error);
    throw error;
  }
};

export async function downloadFromUrl(downloadUrl: string, filename = 'card.pkpass') {
  try {
    // Check if we're on mobile device
    // const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    // const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    console.log('filename', filename)
    // if (isMobile) {
    //   // For mobile devices, use the most reliable method
    //   await openPassForMobile(downloadUrl, filename, isIOS);
    // } else {
    //   // For desktop, download the file
    //   await downloadPassFile(downloadUrl, filename);
    // }
    await downloadPassFile(downloadUrl, filename);
  } catch (error) {
    console.error("Error downloading Apple Wallet pass:", error);
    throw error; // Re-throw to allow error handling in the calling component
  }
}

// async function openPassForMobile(downloadUrl: string, filename: string, isIOS: boolean) {
//   try {
//     // Method 1: Download the pass file first to validate it
//     const response = await api.get(downloadUrl, {
//       responseType: "blob",
//       headers: {
//         Accept: "application/vnd.apple.pkpass",
//       },
//     });
//
//     // Verify the response has the correct MIME type
//     const contentType = response.headers['content-type'];
//     if (!contentType || !contentType.includes('application/vnd.apple.pkpass')) {
//       console.warn('Server returned incorrect MIME type:', contentType);
//       throw new Error('Invalid pass file format');
//     }
//
//     // Verify the blob size (pkpass files should be reasonable size)
//     if (response.data.size === 0) {
//       throw new Error('Empty pass file received');
//     }
//
//     console.log('Pass file downloaded successfully:', {
//       size: response.data.size,
//       contentType: contentType,
//       filename: filename
//     });
//
//     if (isIOS) {
//       // For iOS, create a blob URL and open it
//       const blob = new Blob([response.data], { type: 'application/vnd.apple.pkpass' });
//       const url = window.URL.createObjectURL(blob);
//
//       // Try multiple methods to open the pass
//       try {
//         // Method 1: Direct window.open with blob URL
//         const newWindow = window.open(url, '_blank');
//
//         if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
//           // Method 2: Create a temporary link
//           const link = document.createElement('a');
//           link.href = url;
//           link.download = filename;
//           link.style.display = 'none';
//           document.body.appendChild(link);
//           link.click();
//           document.body.removeChild(link);
//         }
//
//         // Clean up the blob URL after a delay
//         setTimeout(() => {
//           window.URL.revokeObjectURL(url);
//         }, 10000);
//
//       } catch (error) {
//         console.error('Error opening pass with blob URL:', error);
//         throw error;
//       }
//     } else {
//       // For Android, download the file
//       const url = window.URL.createObjectURL(response.data);
//       const a = document.createElement("a");
//       a.href = url;
//       a.download = filename;
//       a.style.display = 'none';
//       document.body.appendChild(a);
//       a.click();
//       a.remove();
//       window.URL.revokeObjectURL(url);
//     }
//
//   } catch (error) {
//     console.error("Error opening pass for mobile:", error);
//     throw error;
//   }
// }

async function downloadPassFile(downloadUrl: string, filename: string) {
  try {
    const response = await api.get(downloadUrl, {
      responseType: "blob",
      headers: {
        Accept: "application/vnd.apple.pkpass",
      },
    });
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    // onContinue();
  } catch (error) {
    console.error("Error downloading Apple Wallet pass:", error);
  }
}